import { useState, useEffect } from 'react';
import { Lock, Globe, Eye, EyeOff, Plus, ArrowLeft } from 'lucide-react';
import type { DocumentSpace } from '@/shared';

// Helper to extract Google Drive Folder ID from a shared link or return raw ID
function extractDriveFolderId(input: string): string {
  const trimmed = input.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const url = new URL(trimmed);
      // Matches /drive/folders/{id}
      if (url.pathname.includes('/drive/folders/')) {
        const parts = url.pathname.split('/drive/folders/');
        if (parts.length > 1) {
          return parts[1].split('/')[0];
        }
      }
      // Matches ?id={id}
      const idParam = url.searchParams.get('id');
      if (idParam) {
        return idParam;
      }
    } catch {
      // Fallback on invalid URL
    }
  }
  return trimmed;
}

interface SpaceManagementProps {
  user: any;
  isAdmin: boolean;
  onBackToDashboard: () => void;
}

export function SpaceManagement({ user, isAdmin, onBackToDashboard }: SpaceManagementProps) {
  const [spaces, setSpaces] = useState<DocumentSpace[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Modal States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSpace, setEditingSpace] = useState<DocumentSpace | null>(null);

  // Form Field States
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDriveInput, setFormDriveInput] = useState('');
  const [formIsVisible, setFormIsVisible] = useState(true);
  const [formDisplayOrder, setFormDisplayOrder] = useState<number>(0);
  const [formParticipantTypes, setFormParticipantTypes] = useState<string[]>([]);
  const [formRoles, setFormRoles] = useState<string[]>([]);

  // Derived Google Drive Folder ID
  const derivedFolderId = extractDriveFolderId(formDriveInput);

  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Update document title on mount
  useEffect(() => {
    document.title = "Document Spaces | Community Documents";
  }, []);

  // Fetch all document spaces for admin
  const fetchAllSpaces = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await fetch('/api/admin/spaces', { credentials: 'include' });
      if (res.status === 401 || res.status === 403) {
        setErrorMsg("Access Denied. You must be an administrator to view this page.");
        return;
      }
      if (!res.ok) {
        throw new Error("Failed to load document spaces.");
      }
      const data = await res.json();
      if (Array.isArray(data)) {
        setSpaces(data);
      }
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || "An unexpected error occurred while loading spaces.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && isAdmin) {
      fetchAllSpaces();
    }
  }, [user, isAdmin]);

  // Open Create Form and pre-populate display order with max(existing) + 1
  const handleOpenCreate = () => {
    setFormError(null);
    setFormName('');
    setFormSlug('');
    setFormDescription('');
    setFormDriveInput('');
    setFormIsVisible(true);
    setFormParticipantTypes([]);
    setFormRoles([]);

    // Calculate max display order + 1
    const nextOrder = spaces.length > 0
      ? Math.max(...spaces.map(s => s.display_order)) + 1
      : 0;
    setFormDisplayOrder(nextOrder);

    setShowCreateModal(true);
  };

  // Open Edit Form and populate values
  const handleOpenEdit = (space: DocumentSpace) => {
    setFormError(null);
    setEditingSpace(space);
    setFormName(space.name);
    setFormSlug(space.slug); // Read-only but populated for rendering
    setFormDescription(space.description || '');
    // Pre-populate with constructed full link for best UX
    setFormDriveInput(space.drive_folder_id ? `https://drive.google.com/drive/folders/${space.drive_folder_id}` : '');
    setFormIsVisible(space.is_visible);
    setFormDisplayOrder(space.display_order);

    const rules = space.access_rules || {};
    setFormParticipantTypes(rules.participant_types || []);
    setFormRoles(rules.roles || []);
  };

  // Handle Create submission
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formName.trim()) return setFormError("Name is required");
    if (!formSlug.trim()) return setFormError("Slug is required");
    if (!/^[a-z0-9-]+$/.test(formSlug)) {
      return setFormError("Slug must contain only lowercase letters, numbers, and hyphens");
    }
    if (!derivedFolderId.trim()) return setFormError("Google Drive folder ID or shared link is required");

    setSubmitting(true);
    try {
      const res = await fetch('/api/admin/spaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formName,
          slug: formSlug,
          description: formDescription,
          drive_folder_id: derivedFolderId,
          is_visible: formIsVisible,
          display_order: formDisplayOrder,
          access_rules: {
            participant_types: formParticipantTypes,
            roles: formRoles
          }
        }),
        credentials: 'include'
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to create space");
      }

      setShowCreateModal(false);
      fetchAllSpaces();
    } catch (err: any) {
      setFormError(err.message || "Failed to create space");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Edit submission
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!editingSpace) return;
    if (!formName.trim()) return setFormError("Name is required");
    if (!derivedFolderId.trim()) return setFormError("Google Drive folder ID or shared link is required");

    setSubmitting(true);
    try {
      const res = await fetch(`/api/admin/spaces/${editingSpace.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formName,
          description: formDescription,
          drive_folder_id: derivedFolderId,
          is_visible: formIsVisible,
          display_order: formDisplayOrder,
          access_rules: {
            participant_types: formParticipantTypes,
            roles: formRoles
          }
        }),
        credentials: 'include'
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to update space");
      }

      setEditingSpace(null);
      fetchAllSpaces();
    } catch (err: any) {
      setFormError(err.message || "Failed to update space");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete space
  const handleDeleteSpace = async (space: DocumentSpace) => {
    const confirmMessage = `Are you sure you want to delete the space "${space.name}"?\n\nThis will remove the space from the dashboard. Files inside the Google Drive folder will not be affected.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const res = await fetch(`/api/admin/spaces/${space.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to delete space");
      }

      fetchAllSpaces();
    } catch (err: any) {
      alert(err.message || "Failed to delete space");
    }
  };

  // Helper to get Google Drive URL
  const getDriveUrl = (folderId: string) => {
    if (!folderId) return '#';
    return `https://drive.google.com/drive/folders/${folderId}`;
  };

  // Render Access Denied
  if (!user || !isAdmin || errorMsg) {
    return (
      <div className="glass-panel text-center" style={{ padding: '3.5rem', marginTop: '2rem' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🔒</div>
        <h3>{errorMsg || "Access Denied"}</h3>
        <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
          This page is restricted to administrators only.
        </p>
        <button className="btn btn-primary mt-4" onClick={onBackToDashboard}>
          Return to Hub
        </button>
      </div>
    );
  }

  return (
    <div className="glass-panel">
      {/* Header section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <button className="btn btn-secondary" onClick={onBackToDashboard} style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            <ArrowLeft size={14} /> Back to Dashboard
          </button>
          <h2 style={{ fontSize: '2.1rem', fontFamily: 'Outfit, sans-serif' }}>Document Spaces</h2>
        </div>
        <button className="btn btn-primary" onClick={handleOpenCreate} id="btn-create-space">
          <Plus size={15} /> Create Space
        </button>
      </div>

      {loading ? (
        <div className="text-center" style={{ padding: '4rem 0' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
          <p style={{ color: 'var(--text-secondary)' }}>Retrieving configuration spaces...</p>
        </div>
      ) : spaces.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📂</div>
          <p>No document spaces configured yet.</p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Google Drive Folder</th>
                <th>Permission</th>
                <th>Visibility</th>
                <th>Order</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {spaces.map((space) => {
                const rules = space.access_rules || {};
                const hasRules = (rules.participant_types?.length || 0) > 0 || (rules.roles?.length || 0) > 0;
                const driveLink = getDriveUrl(space.drive_folder_id);
                return (
                  <tr key={space.id}>
                    <td style={{ fontWeight: 600 }}>{space.name}</td>
                    <td><code>{space.slug}</code></td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>📁 Google Drive Folder</span>
                        <code style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }} title={space.drive_folder_id}>
                          {space.drive_folder_id.length > 15
                            ? `${space.drive_folder_id.substring(0, 10)}...${space.drive_folder_id.substring(space.drive_folder_id.length - 5)}`
                            : space.drive_folder_id}
                        </code>
                        <a
                          href={driveLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '0.78rem', color: 'var(--secondary)', textDecoration: 'none' }}
                        >
                          Open in Google Drive ↗
                        </a>
                      </div>
                    </td>
                    <td>
                      {!hasRules ? (
                        <span className="badge badge-public">🌍 Public</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-start' }}>
                          <span className="badge badge-resident" style={{ fontSize: '0.68rem', padding: '0.15rem 0.4rem' }}>
                            🔒 Restricted
                          </span>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                            {rules.participant_types?.map((type: string) => (
                              <span key={type} className={`badge ${type === 'resident' ? 'badge-owner' : 'badge-staff'}`} style={{ fontSize: '0.65rem', textTransform: 'none' }}>
                                {type === 'resident' ? 'Resident' : 'Non-Resident'}
                              </span>
                            ))}
                            {rules.roles?.map((role: string) => {
                              const normalizedRole = role.replace(/-/g, '_');
                              let badgeClass = 'badge-verifier';
                              let label = role;
                              if (normalizedRole === 'admin') {
                                badgeClass = 'badge-admin';
                                label = 'Admin';
                              } else if (normalizedRole === 'committee') {
                                badgeClass = 'badge-renter';
                                label = 'Committee';
                              } else if (normalizedRole === 'resident_verifier') {
                                badgeClass = 'badge-verifier';
                                label = 'Verifier';
                              } else {
                                label = role.charAt(0).toUpperCase() + role.slice(1);
                              }
                              return (
                                <span key={role} className={`badge ${badgeClass}`} style={{ fontSize: '0.65rem', textTransform: 'none' }}>
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${space.is_visible ? 'badge-published' : 'badge-hidden'}`}>
                        {space.is_visible
                          ? <><Eye size={11} style={{ display: 'inline', marginRight: '3px' }} />Published</>
                          : <><EyeOff size={11} style={{ display: 'inline', marginRight: '3px' }} />Hidden</>
                        }
                      </span>
                    </td>
                    <td>{space.display_order}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleOpenEdit(space)}
                          style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDeleteSpace(space)}
                          style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', background: '#dc3545', borderColor: '#dc3545', color: '#fff' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CREATE MODAL */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Create Document Space</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="modal-body admin-modal-body">
                {formError && <div className="form-error-alert">⚠️ {formError}</div>}

                <div className="form-group">
                  <label htmlFor="create-name" className="form-label">Name</label>
                  <input
                    type="text"
                    id="create-name"
                    className="form-control"
                    placeholder="e.g. Meeting Minutes"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="create-slug" className="form-label">Slug</label>
                  <input
                    type="text"
                    id="create-slug"
                    className="form-control"
                    placeholder="e.g. minutes"
                    value={formSlug}
                    onChange={(e) => setFormSlug(e.target.value)}
                    required
                  />
                  <small className="form-help">Lowercase, letters, numbers, and hyphens only. Used in URLs.</small>
                </div>

                <div className="form-group">
                  <label htmlFor="create-desc" className="form-label">Description (Optional)</label>
                  <textarea
                    id="create-desc"
                    className="form-control"
                    rows={3}
                    placeholder="Describe this space for community residents..."
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="create-folder-link" className="form-label">Google Drive Folder Link / ID</label>
                  <input
                    type="text"
                    id="create-folder-link"
                    className="form-control"
                    placeholder="Paste shared link (e.g. https://drive.google.com/...) or folder ID"
                    value={formDriveInput}
                    onChange={(e) => setFormDriveInput(e.target.value)}
                    required
                  />
                  <small className="form-help">Pasting a full shared link will automatically extract the ID below.</small>
                </div>

                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label htmlFor="create-folder-id" className="form-label">Extracted Folder ID</label>
                    {derivedFolderId && (
                      <a
                        href={getDriveUrl(derivedFolderId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '0.85rem', color: 'var(--secondary)', textDecoration: 'none' }}
                      >
                        Open in Google Drive ↗
                      </a>
                    )}
                  </div>
                  <input
                    type="text"
                    id="create-folder-id"
                    className="form-control"
                    value={derivedFolderId}
                    readOnly
                    disabled
                    style={{ background: 'rgba(255, 255, 255, 0.04)', color: 'var(--text-muted)' }}
                  />
                </div>

                <div className="form-group" style={{ width: '130px' }}>
                  <label htmlFor="create-order" className="form-label">Display Order</label>
                  <input
                    type="number"
                    id="create-order"
                    className="form-control"
                    value={formDisplayOrder}
                    onChange={(e) => setFormDisplayOrder(parseInt(e.target.value, 10) || 0)}
                    required
                  />
                </div>

                <div className="form-group" style={{ marginTop: '1rem', padding: '1rem', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: '8px', background: 'rgba(255,255,255,0.01)' }}>
                  <label className="form-label" style={{ fontWeight: 600, display: 'block', marginBottom: '0.75rem', fontSize: '0.95rem' }}>🛡️ Access Rules (OR Condition)</label>

                  <div style={{ marginBottom: '1rem' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>Allowed Participant Types</span>
                    <div style={{ display: 'flex', gap: '1.25rem' }}>
                      {['resident', 'non_resident'].map(type => (
                        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                          <input
                            type="checkbox"
                            checked={formParticipantTypes.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) setFormParticipantTypes([...formParticipantTypes, type]);
                              else setFormParticipantTypes(formParticipantTypes.filter(t => t !== type));
                            }}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          {type === 'resident' ? 'Resident' : 'Non-Resident'}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>Allowed Roles</span>
                    <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                      {['admin', 'resident-verifier', 'committee'].map(role => (
                        <label key={role} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                          <input
                            type="checkbox"
                            checked={formRoles.includes(role)}
                            onChange={(e) => {
                              if (e.target.checked) setFormRoles([...formRoles, role]);
                              else setFormRoles(formRoles.filter(r => r !== role));
                            }}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          {role === 'resident-verifier' ? 'Resident Verifier' : role.charAt(0).toUpperCase() + role.slice(1)}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="create-visible"
                    checked={formIsVisible}
                    onChange={(e) => setFormIsVisible(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="create-visible" className="form-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
                    Publish to Hub (Visible to residents with permission)
                  </label>
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '1.25rem 1.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Space'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editingSpace && (
        <div className="modal-overlay" onClick={() => setEditingSpace(null)}>
          <div className="modal-content admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Document Space: {editingSpace.name}</h3>
              <button className="modal-close" onClick={() => setEditingSpace(null)}>×</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body admin-modal-body">
                {formError && <div className="form-error-alert">⚠️ {formError}</div>}

                <div className="form-group">
                  <label htmlFor="edit-name" className="form-label">Name</label>
                  <input
                    type="text"
                    id="edit-name"
                    className="form-control"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-slug" className="form-label">Slug (Immutable)</label>
                  <input
                    type="text"
                    id="edit-slug"
                    className="form-control"
                    value={formSlug}
                    disabled
                    style={{ background: 'rgba(255, 255, 255, 0.04)', color: 'var(--text-muted)' }}
                  />
                  <small className="form-help" style={{ color: 'var(--text-muted)' }}>
                    Slugs are permanent because they are used in URLs and shared links.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-desc" className="form-label">Description (Optional)</label>
                  <textarea
                    id="edit-desc"
                    className="form-control"
                    rows={3}
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-folder-link" className="form-label">Google Drive Folder Link / ID</label>
                  <input
                    type="text"
                    id="edit-folder-link"
                    className="form-control"
                    placeholder="Paste shared link or folder ID"
                    value={formDriveInput}
                    onChange={(e) => setFormDriveInput(e.target.value)}
                    required
                  />
                  <small className="form-help">Pasting a full shared link will automatically extract the ID below.</small>
                </div>

                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label htmlFor="edit-folder-id" className="form-label">Extracted Folder ID</label>
                    {derivedFolderId && (
                      <a
                        href={getDriveUrl(derivedFolderId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '0.85rem', color: 'var(--secondary)', textDecoration: 'none' }}
                      >
                        Open in Google Drive ↗
                      </a>
                    )}
                  </div>
                  <input
                    type="text"
                    id="edit-folder-id"
                    className="form-control"
                    value={derivedFolderId}
                    readOnly
                    disabled
                    style={{ background: 'rgba(255, 255, 255, 0.04)', color: 'var(--text-muted)' }}
                  />
                </div>

                <div className="form-group" style={{ width: '130px' }}>
                  <label htmlFor="edit-order" className="form-label">Display Order</label>
                  <input
                    type="number"
                    id="edit-order"
                    className="form-control"
                    value={formDisplayOrder}
                    onChange={(e) => setFormDisplayOrder(parseInt(e.target.value, 10) || 0)}
                    required
                  />
                </div>

                <div className="form-group" style={{ marginTop: '1rem', padding: '1rem', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: '8px', background: 'rgba(255,255,255,0.01)' }}>
                  <label className="form-label" style={{ fontWeight: 600, display: 'block', marginBottom: '0.75rem', fontSize: '0.95rem' }}>🛡️ Access Rules (OR Condition)</label>

                  <div style={{ marginBottom: '1rem' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>Allowed Participant Types</span>
                    <div style={{ display: 'flex', gap: '1.25rem' }}>
                      {['resident', 'non_resident'].map(type => (
                        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                          <input
                            type="checkbox"
                            checked={formParticipantTypes.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) setFormParticipantTypes([...formParticipantTypes, type]);
                              else setFormParticipantTypes(formParticipantTypes.filter(t => t !== type));
                            }}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          {type === 'resident' ? 'Resident' : 'Non-Resident'}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem', fontWeight: 500 }}>Allowed Roles</span>
                    <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                      {['admin', 'resident-verifier', 'committee'].map(role => (
                        <label key={role} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                          <input
                            type="checkbox"
                            checked={formRoles.includes(role)}
                            onChange={(e) => {
                              if (e.target.checked) setFormRoles([...formRoles, role]);
                              else setFormRoles(formRoles.filter(r => r !== role));
                            }}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          {role === 'resident-verifier' ? 'Resident Verifier' : role.charAt(0).toUpperCase() + role.slice(1)}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="edit-visible"
                    checked={formIsVisible}
                    onChange={(e) => setFormIsVisible(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="edit-visible" className="form-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
                    Publish to Hub (Visible to residents with permission)
                  </label>
                </div>
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '1.25rem 1.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setEditingSpace(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
